import { Chip, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import { selectThisWorkstation } from "~/redux/selectors/workstationsSelectors";
import { useGetAutostoreGridsQuery } from "~/redux/warehouse/autostoreGrid.hooks";

const EMPTY_PLACEHOLDER = "—";

type FcGridInfoProps = {
  /** Tighter typography and chips for dense headers (e.g. workstation preview). */
  compact?: boolean;
};

export const FcGridInfo = ({ compact = false }: FcGridInfoProps = {}) => {
  const { t } = useTranslation();
  const selectedFulfillmentCenter = useAppSelector(
    selectUsersFulfillmentCenter
  );
  const selectedWorkstation = useAppSelector(selectThisWorkstation);
  const { data: autostoreGrids } = useGetAutostoreGridsQuery();

  const selectedGrid = autostoreGrids?.find(
    (grid) => grid.autostoreGridId === selectedWorkstation?.autostoreGridId
  );
  const gridTemperatureZone = selectedGrid?.temperatureZones[0];

  const fcLabel = selectedFulfillmentCenter?.name?.trim() || EMPTY_PLACEHOLDER;
  const gridName = selectedWorkstation?.autostoreGridName?.trim();
  const gridLabel =
    gridName ||
    (compact ? EMPTY_PLACEHOLDER : t("no workstation selected for grid"));

  const labelVariant = compact ? "body2" : "subtitle1";
  const stackGap = compact ? 1 : 3;
  const innerGap = compact ? 0.5 : 1;
  const chipSize = compact ? "small" : "medium";

  const gridChipColor = gridName
    ? (gridTemperatureZone?.toLowerCase() as "ambient" | "chilled" | "frozen")
    : undefined;

  return (
    <Stack
      gap={stackGap}
      direction="row"
      flexWrap={compact ? { xs: "wrap", md: "nowrap" } : "wrap"}
      alignItems="center"
      sx={compact ? { minWidth: 0, justifyContent: "flex-end" } : undefined}
    >
      <Stack
        spacing={innerGap}
        direction="row"
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <Typography variant={labelVariant} noWrap component="span">
          {t("fulfillment center")}
        </Typography>
        <Chip label={fcLabel} color="10" size={chipSize} />
      </Stack>
      <Stack
        spacing={innerGap}
        direction="row"
        alignItems="center"
        sx={{ minWidth: 0 }}
      >
        <Typography variant={labelVariant} noWrap component="span">
          {t("grid")}
        </Typography>
        <Chip label={gridLabel} size={chipSize} color={gridChipColor} />
      </Stack>
    </Stack>
  );
};
